// Utilidades para codificacion/decodificacion de datos de ejercicios
// Permite almacenar datos estructurados (parejas, orden, etc.) en los campos existentes

export const PAIR_SEPARATOR = '|||'

// =============================================
// MATCHING (Emparejar)
// =============================================

export interface MatchingPair {
  left: string
  right: string
}

export function encodeMatchingPairs(pairs: MatchingPair[]): string[] {
  return pairs
    .filter(p => p.left.trim() && p.right.trim())
    .map(p => `${p.left}${PAIR_SEPARATOR}${p.right}`)
}

export function decodeMatchingPairs(options: string[]): MatchingPair[] {
  return options.map(opt => {
    const parts = opt.split(PAIR_SEPARATOR)
    return {
      left: parts[0] || '',
      right: parts[1] || ''
    }
  })
}

export function isMatchingEncoded(options: string[]): boolean {
  return options.length > 0 && options.some(o => o.includes(PAIR_SEPARATOR))
}

// =============================================
// ORDERING (Ordenar)
// =============================================

export function encodeOrderingCorrectAnswer(items: string[]): string {
  // Almacena los indices correctos (0,1,2,...) como referencia
  return JSON.stringify(items.map((_, i) => i))
}

export function getOrderingItems(options: string[]): string[] {
  return options.filter(o => o.trim() !== '')
}

// =============================================
// FILL BLANK (Completar espacio)
// =============================================

export const BLANK_MARKER = '___'

export function hasBlankMarker(question: string): boolean {
  return question.includes(BLANK_MARKER)
}

export function splitByBlank(question: string): string[] {
  return question.split(BLANK_MARKER)
}

// =============================================
// SHUFFLE
// =============================================

export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// =============================================
// ANSWER CHECKING
// =============================================

function normalizeText(text: string): string {
  return text.trim().toLowerCase().replaceAll(/[.,;:!?¿¡'"]+$/g, '').trim()
}

export function checkMultipleChoice(userAnswer: string, correctAnswer: string): boolean {
  return normalizeText(userAnswer) === normalizeText(correctAnswer)
}

export function checkFillBlank(
  userAnswer: string,
  correctAnswer: string,
  alternatives: string[]
): boolean {
  const normalized = normalizeText(userAnswer)
  if (normalizeText(correctAnswer) === normalized) return true
  return alternatives.some(alt => normalizeText(alt) === normalized)
}

export function checkMatching(
  userPairs: Record<string, string>,
  encodedOptions: string[]
): { correct: number; total: number; details: Record<string, boolean> } {
  const correctPairs = decodeMatchingPairs(encodedOptions)
  const details: Record<string, boolean> = {}
  let correct = 0

  correctPairs.forEach(pair => {
    const userRight = userPairs[pair.left]
    const isCorrect = userRight !== undefined && normalizeText(userRight) === normalizeText(pair.right)
    details[pair.left] = isCorrect
    if (isCorrect) correct++
  })

  return { correct, total: correctPairs.length, details }
}

export function checkOrdering(userOrder: string[], correctOrder: string[]): boolean {
  if (userOrder.length !== correctOrder.length) return false
  return userOrder.every((item, i) => normalizeText(item) === normalizeText(correctOrder[i]))
}

export function checkTranslation(
  userAnswer: string,
  correctAnswer: string,
  alternatives: string[]
): boolean {
  return checkFillBlank(userAnswer, correctAnswer, alternatives)
}

export type ExerciseType =
  | 'multiple_choice'
  | 'fill_blank'
  | 'ordering'
  | 'matching'
  | 'listening'
  | 'speaking'
  | 'translation'

export interface ExerciseData {
  exercise_type: ExerciseType
  question: string
  options: string[]
  correct_answer: string
  audio_url: string | null
}

export function checkAnswer(
  type: ExerciseType,
  exercise: ExerciseData,
  userAnswer: string | string[] | Record<string, string>
): boolean {
  switch (type) {
    case 'multiple_choice':
    case 'listening':
      return checkMultipleChoice(userAnswer as string, exercise.correct_answer)

    case 'fill_blank':
      return checkFillBlank(
        userAnswer as string,
        exercise.correct_answer,
        exercise.options || []
      )

    case 'matching':
      const result = checkMatching(userAnswer as Record<string, string>, exercise.options || [])
      return result.correct === result.total

    case 'ordering':
      return checkOrdering(userAnswer as string[], exercise.options || [])

    case 'translation':
      return checkTranslation(
        userAnswer as string,
        exercise.correct_answer,
        exercise.options || []
      )

    default:
      return normalizeText(userAnswer as string) === normalizeText(exercise.correct_answer)
  }
}
